import lz from "lzutf8";

import { User } from "model/user";

import { apiOnelinkStats } from "net/requests";
import {
  StatsRgbValues,
  StatColors,
  STATS_MIN_DAYS_TO_LOAD,
  LS_STATS_CACHE,
} from "appConfig";
import { utcAddDays, getUtcDate, daysBetweenUtcDates } from "./date_utils";
import { getLanguageString as _ } from "misc/lang";
import { countries } from "assets/countries";
import { hashCode } from "misc/utils";

import {
  StatsRange,
  StatsUtcItem,
  LoadedStatsPerUtc,
  LoadRange,
  OnelinkStats,
} from "model/stats";
import { Onelink } from "model/onelink";
// See "model/stats" for stats data format

var ls = require("local-storage");

////////////////////////////////////////////
// FIX IndexedDB

const myIndexedDB =
  (window as any).indexedDB ||
  (window as any).mozIndexedDB ||
  (window as any).webkitIndexedDB ||
  (window as any).msIndexedDB;
// const myIDBTransaction = (window as any).IDBTransaction ||
//   (window as any).webkitIDBTransaction ||
//   (window as any).msIDBTransaction || { READ_WRITE: "readwrite" };
// const myIDBKeyRange =
//   (window as any).IDBKeyRange ||
//   (window as any).webkitIDBKeyRange ||
//   (window as any).msIDBKeyRange;

const INDEXED_DB_FORMAT_VERSION = 1;

export interface LoadProps {
  user: User;
  onelinkId: string;
  type: string;
  fromUtc: number;
  throughUtc: number;
  onLoad: Function;
  useCache: boolean;
  data?: LoadedStatsPerUtc[];
  cachedStats?: OnelinkStats;
}

export const loadStats = (props: LoadProps): void => {
  const { onelinkId, type, fromUtc, throughUtc, onLoad, useCache } = props;

  const loadRanges = (allstats: OnelinkStats) => {
    // for (const type of StatsType) {
    const stats: StatsRange = allstats[type];

    const loadRange = getDateRangesToLoad(stats, fromUtc, throughUtc);

    for (const range of loadRange) {
      const props2 = {
        ...props,
        fromUtc: range.fromUtc,
        throughUtc: range.throughUtc,
        cachedStats: allstats,
      };

      loadStatsFromServer(props2);
    }
  };

  const onloadedCachedStats = (allstats: OnelinkStats) => {
    if (onLoad) onLoad(allstats);
    loadRanges(allstats);
  };

  if (useCache) {
    getCachedStats(onelinkId, onloadedCachedStats); //load from useCache
  } else {
    loadRanges({});
  }
};

export const getDateRangesToLoad = (
  stats: StatsRange,
  fromUtc: number,
  throughUtc: number
): LoadRange[] => {
  const dateNow = new Date();
  const utcDateNow = getUtcDate(dateNow);
  if (throughUtc > utcDateNow) throughUtc = utcDateNow;

  if (!stats?.lastLoadDate)
    return [{ fromUtc: fromUtc, throughUtc: throughUtc }];

  const loadedRecently = dateNow.getTime() - stats.lastLoadDate < 3600 * 1000;

  const loadBefore = fromUtc < stats.loadMinUtc;
  const loadAfter = throughUtc > stats.loadMaxUtc;
  const reloadToday = throughUtc >= utcDateNow && !loadedRecently;

  if (!loadBefore && !loadAfter) {
    if (reloadToday)
      return [{ fromUtc: stats.loadMaxUtc, throughUtc: utcDateNow }];
    return [];
  }

  const requestRanges = [];

  if (loadBefore) {
    const requestedDaysBefore = daysBetweenUtcDates(fromUtc, stats.loadMinUtc);
    const requestFromUtc = utcAddDays(
      stats.loadMinUtc,
      -Math.max(requestedDaysBefore, STATS_MIN_DAYS_TO_LOAD)
    );
    requestRanges.push({
      fromUtc: requestFromUtc,
      throughUtc: utcAddDays(stats.loadMinUtc, -1),
    });
  }

  if (loadAfter) {
    requestRanges.push({
      fromUtc: stats.utcEnd,
      throughUtc: utcDateNow,
    });
  }

  return requestRanges;
};

///////////////////////////////////////////////////////////////////////////////

const getCachedStats = (
  onelinkId: string,
  onStatsLoaded: (stats: OnelinkStats) => void
) => {
  if (myIndexedDB) {
    getCachedStats_indexDB(onelinkId, onStatsLoaded);
  } else {
    getCachedStats_localStorage(onelinkId, onStatsLoaded);
  }
};

const saveStats = (onelinkId: string, stats: OnelinkStats): void => {
  if (myIndexedDB) {
    saveStats_indexDB(onelinkId, stats);
  } else {
    saveStats_localStorage(onelinkId, stats);
  }
};

///////////////////////////////////////////////////////////////////////////////

const saveStats_localStorage = (
  onelinkId: string,
  stats: OnelinkStats
): void => {
  const data: string = JSON.stringify(stats);

  const compressed = lz.compress(data, { outputEncoding: "Base64" });

  try {
    ls.set(LS_STATS_CACHE + onelinkId, compressed);
  } catch (err) {}

  //console.log(data.length + " -> " + compressed.length);
};

const getCachedStats_localStorage = (
  onelinkId: string,
  onStatsLoaded: (stats: OnelinkStats) => void
) => {
  const data: any = ls.get(LS_STATS_CACHE + onelinkId);
  if (data) {
    try {
      const decompressed = lz.decompress(data, {
        inputEncoding: "Base64",
        outputEncoding: "String",
      });
      onStatsLoaded && onStatsLoaded(JSON.parse(decompressed));
    } catch (err) {}
  }
  onStatsLoaded && onStatsLoaded({});
};

const openIndexDB = (
  onelinkId: string,
  onsuccess: (db: any) => void,
  onerror?: () => void
) => {
  const request = myIndexedDB.open(
    LS_STATS_CACHE + onelinkId,
    INDEXED_DB_FORMAT_VERSION
  );

  request.onupgradeneeded = function (event: any) {
    const db = event.target.result;
    db.createObjectStore("stats");
  };

  request.onsuccess = (event: any) => {
    const db = event.target.result;
    if (!db.getOb) onsuccess && onsuccess(event.target.result);
  };

  request.onerror = function (event: any) {
    onerror && onerror();
  };
};

const saveStats_indexDB = (onelinkId: string, stats: OnelinkStats): void => {
  const onsuccess = (db: any) => {
    var request = db
      .transaction("stats", "readwrite")
      .objectStore("stats")
      .put(stats, "stats");

    request.onsuccess = () => {
      db.close();
    };
  };

  openIndexDB(onelinkId, onsuccess);
};

const getCachedStats_indexDB = (
  onelinkId: string,
  onStatsLoaded: (stats: OnelinkStats) => void
) => {
  const onerror = () => {
    onStatsLoaded && onStatsLoaded({});
  };

  const onsuccess = (db: any) => {
    var request = db.transaction("stats").objectStore("stats").get("stats");

    request.onsuccess = () => {
      const data = request.result;
      db.close();
      onStatsLoaded && onStatsLoaded(data || {});
    };
    request.onerror = () => {
      db.close();
      onStatsLoaded && onStatsLoaded({});
    };
  };

  openIndexDB(onelinkId, onsuccess, onerror);
};

///////////////////////////////////////////////////////////////////////////////

const loadStatsFromServer = (props: LoadProps): void => {
  const { user, onelinkId, type, fromUtc, throughUtc } = props;

  apiOnelinkStats(user, onelinkId, type, fromUtc, throughUtc)
    .then((response) => {
      onStatsLoadedFromServer({ ...props, data: response.data });
    })
    .catch((error) => {
      console.log(error.response?.data?.error);
    });
};

///////////////////////////////////////////////////////////////////////////////

export const onStatsLoadedFromServer = (props: LoadProps): void => {
  const {
    onelinkId,
    type,
    fromUtc,
    throughUtc,
    onLoad,
    useCache = true,
    data,
    cachedStats,
  } = props;

  if (typeof data !== "object") return;

  const allStats = cachedStats || {};
  const stats: StatsRange = allStats[type] || newStatsRange(type);

  if (data.length > 0) {
    data.sort((a: LoadedStatsPerUtc, b: LoadedStatsPerUtc) => a.utc - b.utc);
    mergeStats(stats, data);
  }

  stats.lastLoadDate = new Date().getTime();

  if (!stats.loadMinUtc || fromUtc < stats.loadMinUtc)
    stats.loadMinUtc = fromUtc;
  if (stats.loadMaxUtc < throughUtc) stats.loadMaxUtc = throughUtc;

  allStats[type] = stats;

  if (useCache) saveStats(onelinkId, allStats);

  if (onLoad) onLoad(allStats);
};

///////////////////////////////////////////////////////////////////////////////

export const newStatsRange = (type: string): StatsRange => {
  const statsRange: StatsRange = {
    type: type,
    utcStart: 0,
    utcEnd: 0,
    stats: [],
    loadMinUtc: 0,
    loadMaxUtc: 0,
    lastLoadDate: 0,
  };
  return statsRange;
};

///////////////////////////////////////////////////////////////////////////////

export const mergeStats = (stats: StatsRange, data: LoadedStatsPerUtc[]) => {
  const firstLoadedUtc = data[0].utc;
  const lastLoadedUtc = data[data.length - 1].utc;

  // Check if loaded stats fully covers cached stats
  if (firstLoadedUtc <= stats.utcStart && stats.utcEnd <= lastLoadedUtc) {
    stats.utcStart = 0;
    stats.utcEnd = 0;
    stats.stats = [];
  }

  if (!stats.utcStart || stats.utcStart < firstLoadedUtc) {
    appendStats(stats, data);
  } else {
    prependStats(stats, data);
  }
};

const appendStats = (stats: StatsRange, data: LoadedStatsPerUtc[]) => {
  var daysBetween: number;

  if (stats.utcStart) {
    daysBetween = daysBetweenUtcDates(stats.utcEnd, data[0].utc);
    // daysBetween expected to be 1.
    if (daysBetween < 1) {
      // Remove any overlap - Always use incoming data
      const removeCount = 1 - daysBetween;
      stats.stats.splice(-removeCount, removeCount);
      stats.utcEnd = utcAddDays(stats.utcEnd, -removeCount);
    }
  } else {
    stats.utcStart = data[0].utc;
    stats.utcEnd = stats.utcStart;
  }

  stats.utcEnd = addStatsFillGaps(stats.stats, stats.utcEnd, stats.type, data);
};

const prependStats = (stats: StatsRange, data: LoadedStatsPerUtc[]) => {
  var daysBetween: number;
  const firstLoadedUtc = data[0].utc;

  var prependStats: StatsUtcItem[] = [];

  var lastUtc = addStatsFillGaps(
    prependStats,
    firstLoadedUtc,
    stats.type,
    data
  );

  daysBetween = daysBetweenUtcDates(lastUtc, stats.utcStart);
  // daysBetween expected to be 1.
  // > 1 => fill gap
  // < 1 => remove overlap

  // fill any gaps
  fillGaps(prependStats, daysBetween);
  // Remove any overlap - Always use incoming data.
  if (daysBetween < 1) stats.stats.splice(0, 1 - daysBetween);

  stats.stats = prependStats.concat(stats.stats);
  stats.utcStart = firstLoadedUtc;
};

const addStatsFillGaps = (
  stats: StatsUtcItem[],
  prevUtc: number,
  type: string,
  data: LoadedStatsPerUtc[]
): number => {
  let lastUtc = prevUtc;
  for (const item of data) {
    const daysBetween = daysBetweenUtcDates(lastUtc, item.utc);
    fillGaps(stats, daysBetween);
    calcAttributes(type, item.stats);
    stats.push(item.stats);
    lastUtc = item.utc;
  }
  return lastUtc;
};

const fillGaps = (stats: StatsUtcItem[], daysBetween: number) => {
  // daysBetween expected to be 1.
  // > 1 => fill gap
  for (var i = 1; i < daysBetween; i++) stats.push({});
};

export const calcAttributes = (type: string, stats: any) => {
  const isDevice = type === "device";
  const isRedirect = type === "redirect";

  if (isDevice || isRedirect) {
    const daySum: any = Object.values(stats).reduce((a: any, b: any) => a + b);
    const total = stats.total || 0;
    stats.ios = (stats.iphone || 0) + (stats.ipad || 0);
    if (isDevice) stats.other = total - (daySum - total);
  }
};

///////////////////////////////////////////////////////////////////////////////

export const sumStats = (
  stats: StatsRange,
  startDate: Date,
  endDate: Date
): StatsUtcItem => {
  if (!stats) return {};
  const utcStart = getUtcDate(startDate);
  const utcEnd = getUtcDate(endDate); //endDate is inclusive

  var startIndex: number = daysBetweenUtcDates(stats.utcStart, utcStart);
  var count: number = daysBetweenUtcDates(utcStart, utcEnd) + 1; //endDate is inclusive

  const sum: StatsUtcItem = {};

  for (var i = 0; i < count; i++) {
    const index = startIndex + i;
    if (index >= stats.stats.length) break;
    const daystats = stats.stats[index];
    sumAllKeys(sum, daystats);
  }
  return sum;
};

export const sumAllKeys = (sum: StatsUtcItem, stats: StatsUtcItem) => {
  for (const key in stats) {
    const value = stats[key];

    if (key in sum) {
      addToSum(sum, key, value);
    } else {
      if (typeof value === "number" || typeof value === "string") {
        sum[key] = value;
      } else {
        sum[key] = { ...value };
      }
    }
  }
  return sum;
};

export const addToSum = (sum: StatsUtcItem, key: string, value: any) => {
  if (typeof sum[key] === "number" && typeof value === "number") {
    sum[key] += value;
  } else if (typeof sum[key] === "object" && typeof value === "object") {
    sumAllKeys(sum[key], value);
  }
};

///////////////////////////////////////////////////////////////////////////////
export const getTitle = (type: string, key: string) => {
  switch (type) {
    case "redirect":
    /* falls through */
    case "device":
      return _("stats", "sum_" + key);
    case "country":
      if (countries.hasOwnProperty(key)) return countries[key];
      if (key === "overflow") return "Countries";
      return "Unknown";
    case "referrer":
      if (key === "overflow") return "Referrers";
      return key;
    case "utm":
      if (key === "overflow") return "Combinations";
      if (key === "") return "[none]";
      return key;
    default:
      return "[n/a]";
  }
};

const _getColor = (value: number, usedColors?: Set<string>) => {
  const colorCount = StatColors.length;
  let idx = value % colorCount;
  let color = StatColors[idx];

  if (usedColors) {
    for (let i = 1; i < colorCount; i++) {
      if (!usedColors.has(color)) return color;
      idx = (idx + 1) % colorCount;
      color = StatColors[idx];
    }
    const r = (value >> 22) >> 2;
    const g = ((value >> 10) & 0xfff) >> 4;
    const b = (value & 0x3ff) >> 2;
    return r + "," + g + "," + b;
  }

  return color;
};

export const getColor = (
  type: string,
  key: string,
  usedColors?: Set<string>
) => {
  switch (type) {
    case "redirect":
    /* falls through */
    case "device":
      return StatsRgbValues[key];
    case "country":
      if (countries.hasOwnProperty(key)) {
        let intval = hashCode(key);
        intval = intval * (intval + 1) * (intval + 2) + intval;
        return _getColor(intval, usedColors);
      }
      if (key === "overflow") return "128,128,128";
      return "0,0,0";
    case "referrer":
    case "utm":
      if (key === "overflow") return "128,128,128";
      const intval = hashCode(key);
      return _getColor(intval, usedColors);
    default:
      return "0,0,0";
  }
};

export const clearStats = () => {
  for (const key in localStorage) {
    if (key.startsWith(LS_STATS_CACHE)) {
      ls.remove(key);
    }
  }

  if (myIndexedDB.databases) {
    myIndexedDB.databases().then((databases: any) => {
      for (const db of databases) {
        const name = (db as any).name;
        myIndexedDB.deleteDatabase(name);
      }
    });
  }
};

/////////////////////////////////////////////////////////////////////////////

export const getAccountStatsOverView = (onelinkList: Onelink[]): Onelink => {
  const t: Onelink = {
    onelinkId: "account",
    data: {},
  };

  t.countRequest = 0;
  t.countDeviceMatched = 0;
  t.countRedirectDownload = 0;
  t.countRedirectFallback = 0;

  for (const ol of onelinkList) {
    t.countRequest += ol.countRequest || 0;
    t.countDeviceMatched += ol.countDeviceMatched || 0;
    t.countRedirectDownload += ol.countRedirectDownload || 0;
    t.countRedirectFallback += ol.countRedirectFallback || 0;
  }

  return t;
};
